// Masks personal data before anything is logged, following the rules in
// specs/004-express-booking-api-pid/contracts/log-masking.md (constitution V).

const MASK = '****';
const REDACTED = '***REDACTED***';

// Every value beneath these keys is personal data.
const MASK_ALL_KEYS = ['contactDetails', 'notificationDetails'];
// Single values that are personal data wherever they appear.
const MASK_VALUE_KEYS = ['shipperVatNumber', 'receiverVatNumber', 'bookingConfirmationEmail', 'logonEmail', 'signatureName'];
const REDACT_KEYS = ['signatureImage', 'documentContent'];
// List items whose `value` is personal data: tax numbers, and API errors echoing a rejected value.
const MASK_ITEM_VALUE_KEYS = ['taxDetails', 'errors'];

const maskAll = (value: any): any => {
  if (value === null || value === undefined || value === '') return value;
  if (Array.isArray(value)) return value.map(maskAll);
  if (typeof value === 'object') {
    return Object.fromEntries(Object.entries(value).map(([key, child]) => [key, maskAll(child)]));
  }
  return MASK;
}

const sanitise = (value: any, parentKey?: string): any => {
  if (value === null || value === undefined || typeof value !== 'object') return value;

  if (Array.isArray(value)) {
    return value.map(item => {
      if (parentKey && MASK_ITEM_VALUE_KEYS.includes(parentKey) && item && typeof item === 'object' && 'value' in item) {
        return { ...sanitise(item), value: maskAll(item.value) };
      }
      return sanitise(item);
    });
  }

  return Object.fromEntries(Object.entries(value).map(([key, child]) => {
    if (child === null || child === undefined) return [key, child];
    if (MASK_ALL_KEYS.includes(key)) return [key, maskAll(child)];
    if (MASK_VALUE_KEYS.includes(key)) return [key, maskAll(child)];
    if (REDACT_KEYS.includes(key)) return [key, REDACTED];
    if (key === 'address' && typeof child === 'object' && !Array.isArray(child)) {
      return [key, Object.fromEntries(Object.entries(child).map(([addressKey, addressValue]) =>
        [addressKey, addressKey === 'countryCode' ? addressValue : maskAll(addressValue)]
      ))];
    }
    return [key, sanitise(child, key)];
  }));
}

// Returns a masked deep copy; the input is never changed.
export function sanitiseForLog<T>(value: T): T {
  return sanitise(value);
}
